/**
 * Return URL helpers — preserves a user's destination across the login redirect
 * triggered by inactivity logout (JWT expiry), and signals the reason to the
 * login page so it can show a "Your session expired" banner.
 *
 * SECURITY: `returnTo` is user-controlled input and a classic open-redirect
 * vector. All consumers MUST route the raw value through `sanitizeReturnTo`.
 * The validator is string-based ONLY — do NOT introduce `new URL()` parsing
 * here: `new URL('//evil.com', 'http://localhost').pathname === '/'` is the
 * kind of false-safe pattern this module exists to avoid.
 */

/** Path the user lands on after login when no (valid) returnTo is available. */
export const DEFAULT_AFTER_LOGIN_PATH = '/dashboard';

/** Hard cap on returnTo length — matches typical browser URL limits. */
const MAX_RETURN_TO_LENGTH = 2048;

/**
 * Validate a `returnTo` query-param value. Returns the value verbatim if it's
 * a safe relative path, or `null` if it's absent, malformed, or a suspected
 * open-redirect attempt.
 *
 * Rules (all must pass — any failure returns null):
 *   - must be a non-empty string (typeof check guards against untyped callers)
 *   - must not be whitespace-only
 *   - must be ≤ 2048 characters
 *   - must start with `/`
 *   - must NOT start with `//` (protocol-relative URL)
 *   - must NOT contain `://` (absolute URL with scheme)
 *   - must NOT contain `\` (backslash bypass — some URL parsers treat as `/`)
 *   - must NOT contain `%2F%2F` (URL-encoded protocol-relative)
 */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length === 0) return null;
  if (raw.trim().length === 0) return null;
  if (raw.length > MAX_RETURN_TO_LENGTH) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  if (raw.includes('://')) return null;
  if (raw.includes('\\')) return null;
  // URL-encoded protocol-relative URL (e.g. "/%2F%2Fevil.com") — a decoded
  // version would start with "//"; reject at the encoded layer too.
  if (raw.toLowerCase().includes('%2f%2f')) return null;
  return raw;
}

/**
 * Build the login redirect URL for an inactivity logout: threads the user's
 * current location into `?returnTo=` and adds `?reason=<reason>` so the login
 * page can show an explanatory banner.
 *
 * Uses URLSearchParams so values are always properly encoded.
 *
 * If the user is already on `/login` we omit `returnTo` — otherwise we'd
 * create a post-login navigation to `/login`, and the user would loop.
 */
export function buildLoginRedirectUrl(
  location: { pathname: string; search?: string; hash?: string },
  reason: 'expired'
): string {
  const params = new URLSearchParams();

  if (location.pathname !== '/login') {
    const returnTo = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
    params.set('returnTo', returnTo);
  }

  params.set('reason', reason);

  return `/login?${params.toString()}`;
}
